/* eslint
    max-classes-per-file: "warn",
    no-async-promise-executor: "warn",
    no-restricted-syntax: ["warn", "ForOfStatement"],
    prefer-const: "warn",
    object-shorthand: "warn",
    prefer-template: "warn",
    @typescript-eslint/no-shadow: "warn",
    block-scoped-var: "warn",
    no-useless-return: "warn",
    no-redeclare: "warn",
    no-else-return: "warn",
    prefer-arrow-callback: "warn"
*/

import { element } from '@shared/easyfy.core';
import { EasyfyEventLogger } from '@shared/easyfy.events/easyfy.event.devlogger';
import { EasyfyEventData, IClientEventMetaData, IDataMapper, IEasyfyEvent, IEasyfyEventExporter, ISessionStarted, ITrackableElement, IWorkerJob, IWorkerMessage } from '@shared/easyfy.events/easyfy.eventdata';
import { Ga4EventExporter } from '@shared/easyfy.events/exporters/eventExporter.ga4';
import { StatsEventExporter } from '@shared/easyfy.events/exporters/eventExporter.stats';
import { FeatureNames, Features } from '@webServices/features.service';
import { KlaviyoEventExporter } from './exporters/eventExporter.klaviyo';

import { EventNames } from './easyfy.eventhandler.eventnames';
import { LocalStorageKeys } from './easyfy.eventhandler.localstoragekeys';
import { Message } from './easyfy.eventhandler.message';
import { ServiceNames } from './easyfy.eventhandler.servicenames';

namespace Easyfy.Modules {

  export class EasyfyEventHandler {
    private eventExporters: Map<string, IEasyfyEventExporter> = new Map<string, IEasyfyEventExporter>();

    private observedElements: string[] = [];

    private intersectionObserver: IntersectionObserver;

    private bannerIntersectionObserver: IntersectionObserver;

    private mutationObserver: MutationObserver;

    public static instance: EasyfyEventHandler;

    private featureFlag: boolean;

    private initiated: boolean = false;

    constructor(eventMappers: Map<string, IDataMapper>) {
      EasyfyEventHandler.instance = this;
      EasyfyEventHandler.instance.featureFlag = Features.FeatureIsActive(FeatureNames.EventTracking);
      window.EasyfyEventLayerInstance = this;

      if (!EasyfyEventHandler.instance.featureFlag) return;

      EasyfyEventData.GetAllServices().forEach(service => {
        let eventExporter: IEasyfyEventExporter;
        switch (service.name.toLowerCase()) {
          case ServiceNames.Stats: {
            eventExporter = new StatsEventExporter();
            break;
          }
          case ServiceNames.Klaviyo: {
            eventExporter = new KlaviyoEventExporter();
            break;
          }
          case ServiceNames.Ga4: {
            eventExporter = new Ga4EventExporter();
            break;
          }
          default: {
            console.warn(`No event exporter found for service: ${service.name}`);
          }
        }
        if (eventExporter) {
          eventExporter.WithMapper(eventMappers.get(service.name));
          this.eventExporters.set(service.name, eventExporter);
        }
      });

      EasyfyEventLogger.Log({
        type: "updateeventlayerstatus",
      });

      EasyfyEventHandler.CheckSessionStart().then(() => {
        EasyfyEventHandler.RunEvents().then(() => {
          if (EasyfyEventData.ShouldRegisterProductImpressions()) {
            EasyfyEventHandler.InitProductIntersectionObserver();
            EasyfyEventHandler.InitMutationObserver();
          }
          if (EasyfyEventData.ShouldRegisterBannerImpressions()) {
            EasyfyEventHandler.InitBannerIntersectionObserver();
          }
          EasyfyEventHandler.InitElementIntersectionObserver();
          window.addEventListener('click', EasyfyEventHandler.ClickEventChecker);
          EasyfyEventHandler.instance.initiated = true;
        });
      });
    };

    static async RunEvents(): Promise<void> {
      return new Promise<void>(async (resolve) => {
        const inlineScript = element('[data-container="easyfy-preload-tracking-events"]');
        if (inlineScript) {
          inlineScript.remove();
        }

        if (!EasyfyEventData.HasEvents()) {
          resolve();
          return;
        }

        for await (const event of EasyfyEventData.Events()) {
          await EasyfyEventHandler.RunEvent(event);
        };

        EasyfyEventData.ClearEvents();
        resolve();
      });
    };

    static async CheckSessionStart(): Promise<void> {
      return new Promise(async (resolve) => {
        if (EasyfyEventData.ServiceIsActive(ServiceNames.Stats) !== true) {
          resolve();
          return;
        }
        let sessionTimer = localStorage.getItem(LocalStorageKeys.SessionTimer);
        if (!sessionTimer) {
          localStorage.setItem(LocalStorageKeys.SessionTimer, new Date(Date.now()).toUTCString());
          sessionTimer = localStorage.getItem(LocalStorageKeys.SessionTimer);
          await EasyfyEventHandler.FetchClientMetaData();
          await EasyfyEventHandler.SendStartSessionEvent();
          resolve();
        }

        const checkTime = new Date(sessionTimer);
        const now = new Date(Date.now());

        let diff = (checkTime.getTime() - now.getTime()) / 1000;
        diff /= 60;
        const minutesBetween = Math.abs(Math.round(diff));

        if (minutesBetween >= 20) {
          const oldSessionId = localStorage.getItem(LocalStorageKeys.SessionId);
          await EasyfyEventHandler.FetchClientMetaData();
          const newSessionId = localStorage.getItem(LocalStorageKeys.SessionId);
          if (oldSessionId !== newSessionId) {
            await EasyfyEventHandler.SendStartSessionEvent();
          }
        }

        localStorage.setItem(LocalStorageKeys.SessionTimer, new Date(Date.now()).toUTCString());

        resolve();
      });
    }

    static async FetchClientMetaData(): Promise<boolean> {
      return new Promise(async (resolve) => {

        EasyfyEventData.SetStatsApiBlock(false);

        const response = await fetch("/api/stats/clientmetadata/");

        if (!response.ok) {
          if (response.status === 428 || response.status === 404) {
            EasyfyEventData.SetStatsApiBlock(true);
          }

          localStorage.setItem(LocalStorageKeys.SessionId, null);
          localStorage.setItem(LocalStorageKeys.ClientId, null);
          localStorage.setItem(LocalStorageKeys.UserId, null);
          localStorage.setItem(LocalStorageKeys.IsDekstop, null);
          localStorage.setItem(LocalStorageKeys.IsMobilePhone, null);
          localStorage.setItem(LocalStorageKeys.IsTablet, null);
          localStorage.setItem(LocalStorageKeys.UserIdCookieKey, null);
          localStorage.setItem(LocalStorageKeys.IPAddress, null);

          resolve(false);
          return;
        }

        const clientMetaData = await response.json();

        localStorage.setItem(LocalStorageKeys.SessionId, clientMetaData.sessionId);
        localStorage.setItem(LocalStorageKeys.ClientId, clientMetaData.clientId);
        localStorage.setItem(LocalStorageKeys.UserId, clientMetaData.userId);
        localStorage.setItem(LocalStorageKeys.IsDekstop, clientMetaData.isDesktop);
        localStorage.setItem(LocalStorageKeys.IsMobilePhone, clientMetaData.isMobilePhone);
        localStorage.setItem(LocalStorageKeys.IsTablet, clientMetaData.isTablet);
        localStorage.setItem(LocalStorageKeys.UserIdCookieKey, clientMetaData.userIdCookieKey);
        localStorage.setItem(LocalStorageKeys.IPAddress, clientMetaData.ipAddress);


        const data: IClientEventMetaData = {
          ClientId: clientMetaData.clientId,
          SessionId: clientMetaData.sessionId,
          UserId: clientMetaData.userId,
          IsDesktop: clientMetaData.isDesktop,
          IsMobilePhone: clientMetaData.isMobilePhone,
          IsTablet: clientMetaData.isTablet,
          IPAddress: clientMetaData.ipAddress
        };
        resolve(true);
      });
    }

    static BlockStatsApi(): void {
      EasyfyEventData.SetStatsApiBlock(true);
    }

    static HandleWorkerMessage(message) {
      const typedMessage = message.data as IWorkerMessage;
      switch (typedMessage.Message) {
        case Message.FetchClientMetaData: {
          EasyfyEventHandler.FetchClientMetaData();
          break;
        }
        case Message.BlockStatsApi: {
          EasyfyEventHandler.BlockStatsApi();
          break;
        }
        default: {
            console.warn(`Unknown worker message: ${typedMessage.Message}`);
        }
      }
    }

    static SendStartSessionEvent(): Promise<void> {
      return new Promise<void>(async (resolve) => {
        const clientMetaData: IClientEventMetaData = EasyfyEventData.GetClientEventMetaData();
        const clientData: ISessionStarted = {
          Culture: "not-set-yet", // todo -> ?
          Currency: EasyfyEventData.SiteInfo().currencyCode,
          IsDesktop: clientMetaData.IsDesktop,
          IsMobilePhone: clientMetaData.IsMobilePhone,
          IsTablet: clientMetaData.IsTablet,
          RecurringUser: EasyfyEventData.IsRecurringUser(true),
          Referrer: document.referrer,
          Path: window.location.pathname,
          UserAgent: window.navigator.userAgent,
          ClientId: clientMetaData.ClientId,
          SessionId: clientMetaData.SessionId,
          UserId: clientMetaData.UserId,
          VisitorId: clientMetaData.UserId
        };

        const sessionStartedEvent: IEasyfyEvent = {
          eventName: EventNames.SessionStarted,
          data: clientData
        };

        await EasyfyEventHandler.RunEvent(sessionStartedEvent);
        resolve();
      });
    }

    static InitMutationObserver(): void {
      const options = {
        childList: true,
        subtree: true,
        attributes: false,
        characterData: false
      };

      const targets = document.querySelectorAll('[data-track-list], [data-container="search-result"]');
      EasyfyEventHandler.instance.mutationObserver = new MutationObserver(this.ElementMutated);
      targets.forEach(target => EasyfyEventHandler.instance.mutationObserver.observe(target, options));
    }

    static ElementMutated(mutations: MutationRecord[]) {
      mutations.forEach(mutation => {
        EasyfyEventHandler.RebindObserveProducts();
      });
    }

    static BannerElementMutated(mutations: MutationRecord[]) {
      mutations.forEach(mutation => {
        EasyfyEventHandler.RebindObserveBanners();
      });
    }

    static InitProductIntersectionObserver(): void {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      };
      EasyfyEventHandler.instance.intersectionObserver = new IntersectionObserver(this.ProductCardObserved, options);
      const targets = document.querySelectorAll('[data-track-product]');
      targets.forEach(target => EasyfyEventHandler.instance.intersectionObserver.observe(target));
    }

    static InitBannerIntersectionObserver(): void {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      };
      EasyfyEventHandler.instance.bannerIntersectionObserver = new IntersectionObserver(this.BannerElementObserved, options);
      const bannerTargets = document.querySelectorAll('[data-track-banner]');
      bannerTargets.forEach(target => EasyfyEventHandler.instance.bannerIntersectionObserver.observe(target));
    }

    static InitElementIntersectionObserver(): void {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      };
      EasyfyEventHandler.instance.bannerIntersectionObserver = new IntersectionObserver(this.TrackableElementObserved, options);
      const bannerTargets = document.querySelectorAll('[data-track-element]');
      bannerTargets.forEach(target => EasyfyEventHandler.instance.bannerIntersectionObserver.observe(target));
    }

    static RebindObserveProducts() {
      EasyfyEventHandler.instance.intersectionObserver.disconnect();
      const targets = document.querySelectorAll('[data-track-product]');
      targets.forEach(target => EasyfyEventHandler.instance.intersectionObserver.observe(target));
    }

    static RebindObserveBanners() {
      EasyfyEventHandler.instance.bannerIntersectionObserver.disconnect();
      const targets = document.querySelectorAll('[data-track-banner]');
      targets.forEach(target => EasyfyEventHandler.instance.bannerIntersectionObserver.observe(target));
    }

    static ProductCardObserved(entries: IntersectionObserverEntry[], observer: any) {
      const eventProductIds: string[] = [];
      const eventProductSalesUnitCodes: string[] = [];
      const listIds: string[] = [];
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          EasyfyEventHandler.instance.intersectionObserver.unobserve(entry.target);
          const elementIsRegisteredForImpression = entry.target.getAttribute("data-track-impression").toLowerCase() === 'true';
          if (!elementIsRegisteredForImpression) return;

          const productIdentifier = entry.target.getAttribute("data-product-event-id");
          const defaultSalesUnitCode = entry.target.getAttribute("data-default-sales-unit-code");

          if (productIdentifier) {
            if (!EasyfyEventHandler.instance.observedElements.includes(productIdentifier + defaultSalesUnitCode)) {
              EasyfyEventHandler.instance.observedElements.push(productIdentifier + defaultSalesUnitCode);
              EasyfyEventLogger.Log({
                type: "productimpression",
                message: (`Product '${productIdentifier}' with SalesCodeUnit '${defaultSalesUnitCode}' seen`),
                target: entry.target
              });
              eventProductIds.push(productIdentifier);
              eventProductSalesUnitCodes.push(defaultSalesUnitCode);
              let node = entry.target;
              while (true) {
                node = node.parentElement;
                if (node.hasAttribute("data-track-list")) {
                  const listId = node.getAttribute("data-list-name");
                  if (listId) {
                    listIds.push(listId);
                    break;
                  }
                }
                else if (node.nodeName === 'BODY') {
                  listIds.push(null);
                  break;
                }
              }
            }
          }
          else {
            EasyfyEventLogger.Log({
              type: "error",
              message: "Product impression failed: missing event product ID"
            });
          }
        }
      });

      if (eventProductIds.length > 0) {
        const event: IEasyfyEvent = {
          eventName: EventNames.ProductImpression,
          data: {
            productIdentifiers: eventProductIds,
            salesUnitCodes: eventProductSalesUnitCodes,
            listIds
          }
        };
        EasyfyEventHandler.RunEvent(event);
      }
    }

    static BannerElementObserved(entries: IntersectionObserverEntry[], observer: any) {
      const bannerIds: string[] = [];
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          EasyfyEventHandler.instance.bannerIntersectionObserver.unobserve(entry.target);
          const elementIsRegisteredForImpression = entry.target.getAttribute("data-track-impression").toLowerCase() === 'true';
          if (!elementIsRegisteredForImpression) return;
          const bannerIdentifier = entry.target.getAttribute("data-banner-event-id");
          if (bannerIdentifier) {
            if (!EasyfyEventHandler.instance.observedElements.includes(bannerIdentifier)) {
              EasyfyEventHandler.instance.observedElements.push(bannerIdentifier);
              EasyfyEventLogger.Log({
                type: "bannerimpression",
                message: (`Banner '${bannerIdentifier}' seen`)
              });
              bannerIds.push(bannerIdentifier);
            }
          }
          else {
            EasyfyEventLogger.Log({
              type: "error",
              message: "Banner impression failed: missing event banner ID"
            });
          }
        }
      });

      if (bannerIds.length > 0) {
        bannerIds.forEach(id => {
          const event: IEasyfyEvent = {
            eventName: EventNames.BannerImpression,
            data: { bannerIdentifier: id }
          };
          EasyfyEventHandler.RunEvent(event);
        });
      }
    }

    static TrackableElementObserved(entries: IntersectionObserverEntry[], observer: any) {
      const elements: ITrackableElement[] = [];
      entries.forEach(entry => {
        if (entry.isIntersecting) {

          const elementIsRegisteredForImpression = entry.target.getAttribute("data-track-impression").toLowerCase() === 'true';
          if (!elementIsRegisteredForImpression) return;

          const elementIdentifier = entry.target.getAttribute("data-event-id");
          if (elementIdentifier) {

            const multiObserve = entry.target.getAttribute("data-multi-observe").toLowerCase() === 'true';
            if (multiObserve === false) {
              EasyfyEventHandler.instance.bannerIntersectionObserver.unobserve(entry.target);
              if (!EasyfyEventHandler.instance.observedElements.includes(elementIdentifier)) {
                EasyfyEventHandler.instance.observedElements.push(elementIdentifier);
              }
            }

            const trackType = entry.target.getAttribute("data-track-type");
            const properties = new Map<string, any>();

            const elementAttributes = entry.target.getAttributeNames();
            elementAttributes.forEach(attr => {
              if(!attr.startsWith("data-event-property-")) {return}
              const key = attr.replace("data-event-property-", "");
              const value = entry.target.getAttribute(attr);
              properties.set(key, value);
            });

            elements.push({
              Id: elementIdentifier,
              Type: trackType,
              Properties: properties
            });

            EasyfyEventLogger.Log({
              type: "elementimpression",
              message: (`Element '${elementIdentifier}' seen`)
            });

          }
          else {
            EasyfyEventLogger.Log({
              type: "error",
              message: "Element impression failed: missing event ID on element"
            });
          }
        }
      });

      if (elements.length > 0) {
        elements.forEach(element => {
          const event: IEasyfyEvent = {
            eventName: EventNames.ElementImpression,
            data: element
          };
          EasyfyEventHandler.RunEvent(event);
        });
      }
    }

    static ClickEventChecker(event: any) {
      if (event.target.closest('A') != null) {
        const node = event.target.closest('A');
        const targetUrl = node.href;
        if (node.closest('[data-track-product]') != null) {
          EasyfyEventHandler.ClickEventProduct(event, node, targetUrl);

        }
        else if (node.closest('[data-track-banner]') != null) {
          EasyfyEventHandler.ClickEventBanner(event, node, targetUrl);

        }
        else if (node.closest('[data-track-element]') != null) {
          EasyfyEventHandler.ClickEventElement(event, node, targetUrl);

        }
      }
      else {
        const node = event.target;
        if (node.closest('[data-track-element]') != null) {
          EasyfyEventHandler.ClickEventElement(event, node);

        }
      }
    }

    static ClickEventProduct(event: any, node: any, targetUrl: any) {
      node = node.closest('[data-track-product]');
      const elementIsRegisteredForClick = node.getAttribute("data-track-click").toLowerCase() === 'true';
      if (elementIsRegisteredForClick) {
        event.stopPropagation();
        event.preventDefault();

        const defaultSalesUnitCode = node.getAttribute("data-default-sales-unit-code");
        const productId = node.getAttribute('data-product-event-id');
        if (productId) {
          let listName;
          while (true) {
            node = node.parentElement;
            if (!node) {
              break;
            }
            if (node.hasAttribute("data-track-list")) {
              listName = node.getAttribute("data-list-name");
              break;
            }
            else if (node.nodeName === 'body') {
              listName = null;
              break;
            }
          }

          const event: IEasyfyEvent = {
            eventName: EventNames.ProductClick,
            data: {
              productIdentifier: productId,
              salesUnitCode: defaultSalesUnitCode,
              listName
            }
          };
          EasyfyEventHandler.RunEvent(event);
          window.location = targetUrl;
        }
        else {
          // Don't block links
          window.location = targetUrl;
        }
      }
    }

    static ClickEventBanner(event: any, node: any, targetUrl: any) {
      node = node.closest('[data-track-banner]');
      const elementIsRegisteredForClick = node.getAttribute("data-track-click").toLowerCase() === 'true';
      if (elementIsRegisteredForClick) {
        event.stopPropagation();
        event.preventDefault();

        const bannerId = node.getAttribute('data-banner-event-id');
        if (bannerId) {
          const event: IEasyfyEvent = {
            eventName: EventNames.BannerClick,
            data: {
              bannerIdentifier: bannerId,
            }
          };
          EasyfyEventHandler.RunEvent(event);
          window.location = targetUrl;
        }
        else {
          // Don't block links
          window.location = targetUrl;
        }
      }
    }

    static ClickEventElement(event: any, node: any, targetUrl?: any) {
      node = node.closest('[data-track-element]');
      const elementIsRegisteredForClick = node.getAttribute("data-track-click")?.toLowerCase() === 'true' ?? false;
      if (elementIsRegisteredForClick) {

        if (targetUrl) {
          event.stopPropagation();
          event.preventDefault();
        }

        const elementId = node.getAttribute('data-event-id');

        if (elementId) {
          const trackType = node.getAttribute('data-track-type');
          const properties = new Map<string, any>();

          for (const attribute of node.getAttributeNames()) {
            if (attribute.startsWith("data-event-property-")) {
              const value = node.getAttribute(attribute);
              const key = attribute.replace("data-event-property-", "");
              properties.set(key, value);
            }
          }

          const data: ITrackableElement = {
            Id: elementId,
            Type: trackType,
            Properties: properties
          };

          const event: IEasyfyEvent = {
            eventName: EventNames.ElementClick,
            data
          };

          EasyfyEventHandler.RunEvent(event);

          if (targetUrl) {
            window.location = targetUrl;
          }
        }
        else if (targetUrl) {
          // Don't block links
          window.location = targetUrl;
        }
      }
    }

    static GetCurrentInstance(): EasyfyEventHandler {
      let instance: EasyfyEventHandler;
      if (EasyfyEventHandler.instance) {
        instance = EasyfyEventHandler.instance;
      }
      else if (window.EasyfyEventLayerInstance) {
        instance = window.EasyfyEventLayerInstance as EasyfyEventHandler;
      }

      return instance;
    }

    static IsAutomatedBrowser(): boolean {
      return navigator.webdriver === true;
    }

    static RunEvent(event: IEasyfyEvent): Promise<void> {
      return new Promise<void>(async (resolve) => {
        if (EasyfyEventHandler.IsAutomatedBrowser()) {
          resolve();
          return;
        }

        const dispatchedToServices = [];
        const errors = new Map<string, string>();
        for await (const service of EasyfyEventData.GetAllServices()) {
          if (EasyfyEventData.ConsumesEvent(service.name, event.eventName)) {
            try {
              const instance = EasyfyEventHandler.GetCurrentInstance();

              await instance.eventExporters.get(service.name)?.RegisterEvent(event);
              dispatchedToServices.push(service.name);

            }
            catch (error) {
              errors.set(service.name, event.eventName);
              console.warn(error);
            }
          }
        };

        if (dispatchedToServices.length > 0) {
          EasyfyEventLogger.Log({ type: "event", message: `Event '${event.eventName}' dispatched to: ${dispatchedToServices.join(', ')}` });
        } else {
          EasyfyEventLogger.Log({ type: "event", message: `Event '${event.eventName}' fired but not dispatched to any services` });
        }
        errors.forEach((value, key) => {
          EasyfyEventLogger.Log({ type: "error", message: `Error when trying to dispatch event '${value}' to: ${key}` });
        });

        resolve();
      });
    }

    static async RegisterResponseData(responseData: any): Promise<void> {
      return new Promise(async (resolve) => {
        const instance = EasyfyEventHandler.GetCurrentInstance();
        if (instance?.initiated) {
          await EasyfyEventHandler.CheckSessionStart();
          if (responseData?.data?.eventLayer) {
            EasyfyEventLogger.Log({
              message: "Event layer data added from response",
            });

            EasyfyEventData.AddDataToEventLayer(responseData.data.eventLayer);

            EasyfyEventLogger.Log({
              type: "updateeventlayerstatus",
            });
            await EasyfyEventHandler.RunEvents();
          }
        }
        resolve();
      });
    }

  }
}

export default Easyfy.Modules;
export { EventNames } from './easyfy.eventhandler.eventnames';
export { LocalStorageKeys } from './easyfy.eventhandler.localstoragekeys';
export { Message } from './easyfy.eventhandler.message';
export { ServiceNames } from './easyfy.eventhandler.servicenames';

